import { ref, computed, Ref } from '@vue/composition-api';
import { get, debounce } from 'lodash-es';
import {
  useCreativeFormItemPrecheck,
  useStdCreativeAdgroupIs,
  useCreativeActionTypeIs,
  useCreativeIs,
  useCreativeExtendStore,
  // TODO 届时放到Store里
  useGetChallengeAuditApi,
  useGetRealTimePrecheckApi,
  useWebsiteFormStatus,
  useGetUrlEventIsEffectiveApi,
} from '@tt4b/ttam-hooks';
import {
  CHALLENGE_AUDIT,
  AdgroupFormData,
  CampaignFormData,
  NormalCreative,
  AcoFormData,
  FullNormalFormData,
} from '@tt4b/ttam-enums';
import { i18n } from '@tt4b/ttam-i18n';
import { useCheckURLStore } from './store';
import { useCheckUrlEffect } from './use-check-url-effect';
import { trackEvent } from './../../../../utils/index';

// 三方
const threeParty = 'ad.doubleclick.net';
/**
 * 检查落地页
 * 1. URL是否https开头
 * 2. URL是否可用
 * 3. only 原生广告：落地页链接是否含有非商业挑战赛
 */
// eslint-disable-next-line max-lines-per-function
export const useCheckUrl = (options: {
  createMode: Ref<string>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  normalCreativeData: Ref<NormalCreative>;
  activeSplitTestIndex: Ref<number>;
  creativeData: Ref<AcoFormData & FullNormalFormData>;
}) => {
  const {
    adgroupData: adgroupFormData,
    campaignData: campaignFormData,
    normalCreativeData,
    activeSplitTestIndex,
    createMode,
    creativeData: creativeFormData,
  } = options;

  const { isNative } = useCreativeIs({
    adgroupFormData,
    campaignFormData,
    normalCreativeData,
    createMode,
  });
  const { isEdit } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData,
  });
  const { isAco } = useStdCreativeAdgroupIs({
    adgroupFormData,
    campaignFormData,
  });
  // 非ACO存储在扩展中，需要和adSoure和adtype联动
  const { creativeExtendAdSourceAdTypeGet, creativeExtendAdSourceAdTypeSet } = useCreativeExtendStore({
    adgroupFormData,
    campaignFormData,
    activeSplitTestIndex,
    normalCreativeData,
  });
  // ACO存储在自己的store中
  const { checkTitleStoreSet, checkURLStoreGet } = useCheckURLStore();

  // 是否显示落地页
  const { isShowExternalUrl } = useWebsiteFormStatus({
    campaignFormData,
    adgroupFormData,
    normalCreativeData,
    createMode,
    creativeFormData,
  });

  const isChecking = ref(false);
  // 是否采用tips内置loading
  // 目前url预校验有两种loading可以使用，一种是tips外置loading，一种是tips内置loading
  // 详情见：https://www.figma.com/file/RUrPSBRVdZzCNjPkSVPyov/TTMP-21-7-12%E6%9C%88-%E7%8E%8B%E8%93%89?node-id=7459%3A33639
  const isUseInTipsLoading = ref(false);

  const urlSuggtionGlobalOperation = {
    pushURLSuggtionList: item => {
      const tryGetSuggtionList = isAco.value
        ? checkURLStoreGet('urlSuggtionList')
        : creativeExtendAdSourceAdTypeGet('urlSuggtionList');
      let suggestionList = tryGetSuggtionList ? tryGetSuggtionList : [];
      suggestionList.push(...item);
      suggestionList = Array.from(new Set(suggestionList));
      isAco.value
        ? checkTitleStoreSet('urlSuggtionList', suggestionList)
        : creativeExtendAdSourceAdTypeSet('urlSuggtionList', suggestionList);
    },
    setURLSuggtionList: val => {
      isAco.value
        ? checkTitleStoreSet('urlSuggtionList', val)
        : creativeExtendAdSourceAdTypeSet('urlSuggtionList', val);
    },
  };
  const computedURLSuggtionList = computed(() =>
    isAco.value ? checkURLStoreGet('urlSuggtionList') : creativeExtendAdSourceAdTypeGet('urlSuggtionList') ?? [],
  );

  const handleChallengeResult = (result, val) => {
    if (result?.urls?.length) {
      const checkInfo = result?.urls?.find(item => item.resource === val);
      if (get(checkInfo, 'challenge_audit_status') === CHALLENGE_AUDIT.URL_HAS) {
        urlSuggtionGlobalOperation.pushURLSuggtionList([i18n.t('module_native_tiktok_video_url_has_unpaid_hashtag')]);
      }
    }
  };

  const handlePrecheckUrl = result => {
    const suggestion = get(result, 'summaryResults', []);

    if (suggestion?.length >= 1) {
      // 补充埋点相关
      trackEvent('module_ad_url_check_fail', {
        url_suggestion: 'invalid',
      });
    }

    urlSuggtionGlobalOperation.pushURLSuggtionList(suggestion);
    const { isClickSubmit } = useCreativeFormItemPrecheck();
    isClickSubmit.value = false;
  };

  const handleHasPostBackEvent = result => {
    if (result && result.hasPostBack === false) {
      trackEvent('ad_creation_creative_url_no_pixel_data');
      urlSuggtionGlobalOperation.pushURLSuggtionList([i18n.t('ad_creation_creative_url_no_pixel_data')]);
    }
  };

  /**
   * 检查是否包含挑战赛以及url预校验
   * @param type 检查的是 标题 、还是 落地页
   * @param val 对应的值
   */
  const getCheckUrlInfo = async (val: string) => {
    try {
      isChecking.value = true;
      if (!val) {
        return;
      }

      const promiseList = [
        useGetRealTimePrecheckApi({
          materials: [{ urls: [val], adsTexts: [] }],
          countries: adgroupFormData.value.country,
        }).then(handlePrecheckUrl),
      ];
      if (isNative.value) {
        promiseList.push(
          useGetChallengeAuditApi({ materials: { urls: [val], titles: [] } }).then(result =>
            handleChallengeResult(result, val),
          ),
        );
      }
      if (isShowExternalUrl.value && adgroupFormData.value.adRefPixelId && adgroupFormData.value.externalAction) {
        promiseList.push(
          useGetUrlEventIsEffectiveApi({
            externalUrl: val,
            pixelId: adgroupFormData.value.adRefPixelId,
            externalAction: adgroupFormData.value.externalAction,
          }).then(handleHasPostBackEvent),
        );
      }
      await Promise.all(promiseList);

      // 操作完成
    } catch (error) {
      urlSuggtionGlobalOperation.setURLSuggtionList([]);
    } finally {
      isChecking.value = false;
    }
  };
  const { setIsStartWithHTTPS, computedIsUrlStartWithHttps } = useCheckUrlEffect({
    adgroupData: adgroupFormData,
    campaignData: campaignFormData,
    activeSplitTestIndex,
    normalCreativeData,
  });

  // 避免太过频繁的请求
  const DEBOUNCE_WAIT_TIME = 300;
  // 调用接口进行校验
  const debounceCheckUrl = debounce(getCheckUrlInfo, DEBOUNCE_WAIT_TIME);
  // urlcheck 相关的方法都汇总到这里

  const urlCheck = externalUrl => {
    // 记录埋点数据
    const eventTrackList = [] as string[];

    /** 新的校验开始之前，先清空旧的校验信息 */
    // 清空之前的建议啊
    urlSuggtionGlobalOperation.setURLSuggtionList([]);
    // 重置是否使用内置loading
    isUseInTipsLoading.value = false;

    /** 然后进行URL相关的同步校验 */
    // 校验是否是https开头
    setIsStartWithHTTPS(externalUrl);
    // 校验用户是否是编辑场景，如果是编辑场景，给出一个提示，谨慎修改url
    if (isEdit.value) {
      urlSuggtionGlobalOperation.pushURLSuggtionList([i18n.t('module_ad_url_edit_tips')]);
    }
    // 校验url中是否包含三方,如果有三方，则先展示三方的suggestion
    if (externalUrl.indexOf(threeParty) >= 0) {
      urlSuggtionGlobalOperation.pushURLSuggtionList([i18n.t('module_ad_url_contain_tripartite_tips')]);
      eventTrackList.push('3party');
    }
    // 如果url并非https或者http开头
    if (!externalUrl.startsWith('http') && !externalUrl.startsWith('https')) {
      urlSuggtionGlobalOperation.pushURLSuggtionList([i18n.t('module_ad_url_not_start_with_http_or_https')]);
      eventTrackList.push('start');
    } else if (!computedIsUrlStartWithHttps.value) {
      // 如果url是以http开头的
      urlSuggtionGlobalOperation.pushURLSuggtionList([i18n.t('module_ad_url_start_with_http')]);
      eventTrackList.push('http');
    }
    // 如果同步校验出现问题，启用内置loading
    if (computedURLSuggtionList.value?.length >= 1) {
      isUseInTipsLoading.value = true;
      // 处理埋点上报
      trackEvent('module_ad_url_check_fail', {
        url_suggestion: eventTrackList?.toString(),
      });
    }

    /** 然后开始URL的异步调用接口校验 */
    debounceCheckUrl(externalUrl);
  };

  const checkExternalUrl = externalUrl => {
    // 跟PM对齐，ACO暂时不接入实时预校验
    // if (isAco.value) return;

    if (externalUrl) {
      // urlcheck 相关的方法都汇总到这里
      urlCheck(externalUrl);
    } else {
      // url为空，也认为有效，表单校验优选 //isURLEffective
      urlSuggtionGlobalOperation.setURLSuggtionList([]);
      // url为空，也清除提示
      setIsStartWithHTTPS('');
    }
  };

  return {
    /** 正在检查中 */
    isChecking,
    /** 是否展示提示信息 */
    computedURLSuggtionList,
    checkExternalUrl,
    /** 是否启用url suggestion 内置loading */
    isUseInTipsLoading,
    setIsStartWithHTTPS,
    computedIsUrlStartWithHttps,
  };
};
